// TOEFL iBT Mock 7 — Listening (2026 format, multistage adaptive, advanced)
//
// Task types:
//   response     – Listen and Choose a Response: hear one line, pick the best reply.
//   conversation – Listen to a Conversation (campus / everyday life).
//   announcement – Listen to an Announcement (campus or public notice).
//   talk         – Listen to an Academic Talk (short lecture).
//
// Mock 7 leans on indirect replies, implied meaning, speaker attitude and
// "why does the speaker mention…" questions. Wrong options often reuse
// words from the audio to punish word-matching.
//
// Each recording plays once. Questions appear after it finishes.
// gender ("m" / "f") picks the synthesized voice for each line.

export const MODULE_SECONDS = { module1: 15 * 60, module2: 12 * 60 };

export const TASK_LABELS = {
  response: "Choose a Response",
  conversation: "Conversation",
  announcement: "Announcement",
  talk: "Academic Talk",
};

const W = (text) => ({ speaker: "Woman", gender: "f", text });
const M = (text) => ({ speaker: "Man", gender: "m", text });

const response = (id, line, options, correct) => ({
  id,
  type: "response",
  lines: [line],
  questions: [{ question: "Choose the best response.", options, correct }],
});

export const listeningModules = {
  module1: [
    response("m1-r1", W("You wouldn't happen to have a spare charger, would you?"), [
      "I'm happy to charge you for it.",
      "It happened while I was charging it.",
      "Only for a laptop, if that's any use.",
      "No, I wouldn't have spared it.",
    ], 2),
    response("m1-r2", M("I thought the reading for this week was optional."), [
      "Only the second article. The first one will be on the quiz.",
      "I read it last week, actually.",
      "It's optional to think about it.",
      "This week's weather has been terrible.",
    ], 0),
    response("m1-r3", W("Professor Grant is letting us resubmit the essay, isn't she?"), [
      "She submitted it before the deadline.",
      "The essay was about letting go.",
      "Yes, I let her know about it.",
      "Only if we got below a C the first time.",
    ], 3),
    response("m1-r4", M("I can't make heads or tails of these lab instructions."), [
      "Flip a coin, then.",
      "Let's go through them together after lunch.",
      "The lab is at the head of the hallway.",
      "I made them myself last year.",
    ], 1),
    response("m1-r5", W("Shouldn't you be at your shift at the library by now?"), [
      "I'd rather shift to a different library.",
      "The library opens at nine.",
      "I swapped with Dana. I'm working tonight instead.",
      "Yes, I should be studying there now.",
    ], 2),
    response("m1-r6", M("If I'd known the seminar was full, I wouldn't have walked all the way over here."), [
      "You should have checked the online sign-up sheet.",
      "It's a long walk to the seminar room.",
      "I didn't know you liked walking.",
      "The seminar will be full next time.",
    ], 0),
    response("m1-r7", W("I'm not sure the data really support the conclusion in your paper."), [
      "I concluded the paper last night.",
      "The data were collected in May.",
      "Thanks for supporting me.",
      "Which part seems weakest to you?",
    ], 3),
    response("m1-r8", M("Any chance the department will extend the application deadline?"), [
      "They extended the building last year.",
      "I doubt it. They said it was final.",
      "The application is two pages long.",
      "There's a good chance of rain.",
    ], 1),
    {
      id: "m1-conv",
      type: "conversation",
      title: "Study Abroad Office",
      lines: [
        M("Hi, I'm hoping to spend next spring in Lisbon, but I just noticed the application asks for a language certificate."),
        W("Right. For Portugal, the partner university requires intermediate Portuguese. Do you have any coursework in the language?"),
        M("Two semesters, but I took them three years ago. I'm worried I've forgotten most of it."),
        W("Well, coursework counts only if it's from the last two years. But you have another option: the language center runs a placement test every month. If you pass at the intermediate level, that satisfies the requirement."),
        M("And if I don't pass?"),
        W("Then you'd still be eligible for the English-taught program in Lisbon, though the course selection is much narrower. Honestly, most students in your position take an intensive summer course first. It's more work, but it keeps all your options open."),
        M("I suppose that makes sense. When's the next placement test?"),
        W("The fifteenth. But I'd suggest spending a few weeks reviewing before you sign up. You can only take it twice per year."),
      ],
      questions: [
        {
          question: "Why can't the man use his previous coursework to meet the requirement?",
          options: [
            "He did not complete the second semester.",
            "The courses were taken too long ago.",
            "The courses were in a different language.",
            "His grades were too low.",
          ],
          correct: 1,
        },
        {
          question: "What does the woman imply about the English-taught program?",
          options: [
            "It is more difficult to get into.",
            "It is only available in the summer.",
            "It offers fewer course choices.",
            "It does not require a placement test.",
          ],
          correct: 2,
        },
        {
          question: "Why does the woman advise the man not to take the next placement test right away?",
          options: [
            "The number of attempts he can make is limited.",
            "The test on the fifteenth is already full.",
            "He must finish a summer course first.",
            "The test is only offered twice a year.",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m1-ann",
      type: "announcement",
      title: "Research Ethics Announcement",
      lines: [
        W("This is a message for all graduate students conducting research with human participants. As of the first of next month, the university's ethics review process is moving to a new online system. Applications already under review will be completed under the old process, so you don't need to resubmit them. However, any amendment to an approved study, even a minor change such as adding a survey question, must now go through the new system. Please note that review times are expected to be longer than usual during the first two months, so if your data collection depends on approval, we strongly recommend submitting at least six weeks in advance. Training sessions on the new system will be held every Wednesday this month in the graduate center."),
      ],
      questions: [
        {
          question: "What must students do if they want to add a question to an approved survey?",
          options: [
            "Wait until the old system is restored",
            "Submit the change through the new online system",
            "Resubmit their entire original application",
            "Attend a training session before making the change",
          ],
          correct: 1,
        },
        {
          question: "Why does the speaker recommend submitting six weeks in advance?",
          options: [
            "Approval is likely to take longer than normal at first.",
            "The new system only accepts applications once a month.",
            "Training sessions must be completed before submitting.",
            "Applications under the old process will be rejected.",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m1-talk",
      type: "talk",
      title: "Geology: Dating Earthquakes with Trees",
      lines: [
        M("When geologists want to know how often a fault produces large earthquakes, they face an obvious problem: written records in many regions go back only a century or two, while the gap between major earthquakes can be several hundred years. So researchers have turned to an unexpected source of evidence: trees. When a strong earthquake strikes, it can tilt trees, crack their roots, or suddenly drop the ground they're standing on. A tree that survives this kind of damage usually responds by changing how it grows, and that change is recorded in its annual growth rings. For example, a tree that has been tilted will start producing much wider rings on one side as it tries to straighten itself. By counting rings back from the present, researchers can identify the exact year the disturbance began. Now, a single damaged tree doesn't prove much. A storm or a landslide could cause the same pattern. What makes the method convincing is when dozens of trees along the same fault all show disruption beginning in the same year. In the Pacific Northwest, this technique helped confirm that a massive earthquake occurred in the winter of seventeen hundred, a date that was later matched with records of a tsunami that reached the coast of Japan."),
      ],
      questions: [
        {
          question: "What problem with historical records does the professor mention?",
          options: [
            "They often exaggerate the size of earthquakes.",
            "They do not cover enough time to show long-term patterns.",
            "They were mostly written in Japan.",
            "They are not precise about the location of faults.",
          ],
          correct: 1,
        },
        {
          question: "According to the professor, what happens to a tree that has been tilted?",
          options: [
            "It stops producing growth rings for several years.",
            "Its roots grow deeper into the ground.",
            "It grows wider rings on one side.",
            "It grows more slowly on all sides.",
          ],
          correct: 2,
        },
        {
          question: "Why does the professor mention storms and landslides?",
          options: [
            "To explain why evidence from many trees is needed",
            "To describe events that often follow earthquakes",
            "To show that trees rarely survive natural disasters",
            "To compare the strength of different natural forces",
          ],
          correct: 0,
        },
        {
          question: "What does the professor imply about the earthquake of seventeen hundred?",
          options: [
            "It was first discovered through Japanese records alone.",
            "It did not affect the trees in the Pacific Northwest.",
            "It was smaller than geologists originally believed.",
            "Its date is supported by two independent kinds of evidence.",
          ],
          correct: 3,
        },
      ],
    },
  ],

  // ── Harder Module 2 ──────────────────────────────────────────
  upper: [
    response("up-r1", M("I'd have thought you'd be thrilled about the scholarship."), [
      "I thought so too, but it means moving across the country.",
      "Yes, I'd thought about applying for it.",
      "The scholarship was thrilling to read about.",
      "No, I've never been on a thrill ride.",
    ], 0),
    response("up-r2", W("It's not as though the committee didn't warn us about the budget."), [
      "The committee meets in the budget office.",
      "I didn't know you were on the committee.",
      "True. We just didn't expect cuts this deep.",
      "They warned us to bring an umbrella.",
    ], 2),
    response("up-r3", M("Funny, I could have sworn the meeting was in Room 12."), [
      "It was funny, wasn't it?",
      "Twelve people were at the meeting.",
      "Don't swear in the meeting room.",
      "It was, until they moved it this morning.",
    ], 3),
    response("up-r4", W("Not that I'm complaining, but the new schedule leaves me no time for lunch."), [
      "Why don't you bring something to eat in class?",
      "Lunch is served in the new building.",
      "I didn't complain about the schedule.",
      "The schedule was published at noon.",
    ], 0),
    response("up-r5", M("Had you mentioned the error sooner, we could have fixed it before printing."), [
      "I printed it as soon as I could.",
      "I only noticed it after the copies arrived.",
      "The error was in the third paragraph.",
      "I mentioned the printer was fixed.",
    ], 1),
    {
      id: "up-conv",
      type: "conversation",
      title: "Lab Supervisor Meeting",
      lines: [
        W("Dr. Brennan, I wanted to talk about the results from the soil samples. They don't match the pattern we predicted."),
        M("Don't match how? Are the nitrogen levels off?"),
        W("They're about thirty percent lower than in last year's samples from the same plots. At first I assumed I'd made a mistake with the equipment, so I re-ran half of them. Same results."),
        M("Hmm. Did anything change in how the samples were collected?"),
        W("Well, this year we collected them in late October instead of September, because of the delay with the field permits."),
        M("That might be your answer right there. Microbial activity drops as the soil cools, and that affects how much nitrogen is available. I wouldn't call that a mistake, but it does mean you can't compare the two years directly."),
        W("So should I throw out this year's data?"),
        M("Not at all. Write up the timing difference as a limitation, and if you can get back out there next September, collect a smaller set from the same plots. Then you'll have something meaningful to compare."),
      ],
      questions: [
        {
          question: "Why did the woman re-run some of the samples?",
          options: [
            "Her supervisor asked her to check the equipment.",
            "She suspected the unexpected results were caused by her own error.",
            "Half of the original samples were damaged.",
            "The field permits required a second analysis.",
          ],
          correct: 1,
        },
        {
          question: "What does the professor suggest is the likely cause of the difference?",
          options: [
            "A change in when the samples were collected",
            "A fault in the laboratory equipment",
            "Contamination of last year's samples",
            "A mistake in the location of the plots",
          ],
          correct: 0,
        },
        {
          question: "What is the professor's attitude toward this year's data?",
          options: [
            "He thinks they should be replaced with last year's data.",
            "He believes they prove the original prediction was wrong.",
            "He considers them useful if their limitation is acknowledged.",
            "He doubts that they were collected from the correct plots.",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "up-talk",
      type: "talk",
      title: "Psychology: The Testing Effect",
      lines: [
        W("Ask most students how they prepare for an exam, and they'll describe some form of re-reading: going over their notes, highlighting the textbook, reviewing slides. It feels productive, because the material becomes more familiar each time. But familiarity is misleading. Recognizing something when you see it is very different from being able to recall it when you don't. In a well-known series of studies, students read a short scientific text. One group then re-read it several times; another group read it once and then practiced writing down everything they could remember, without looking. When tested five minutes later, the re-reading group did slightly better. But when tested a week later, the pattern reversed, and the group that had practiced recalling remembered substantially more. This is called the testing effect, or retrieval practice. The act of pulling information out of memory seems to strengthen it far more than putting it in again. What's striking is that, when asked to predict their own performance, the re-reading students were more confident. In other words, the strategy that felt most effective was actually the less effective one, which may explain why so many students keep using it."),
      ],
      questions: [
        {
          question: "What is the main point of the talk?",
          options: [
            "Students should study in groups rather than alone.",
            "Practicing recall improves long-term memory more than re-reading.",
            "Highlighting is the most effective way to study a textbook.",
            "Short tests cause students to feel overconfident.",
          ],
          correct: 1,
        },
        {
          question: "Why does the professor mention the test given five minutes after studying?",
          options: [
            "To show that retrieval practice works immediately",
            "To explain why the second test was cancelled",
            "To contrast short-term results with long-term results",
            "To prove that re-reading is always ineffective",
          ],
          correct: 2,
        },
        {
          question: "According to the professor, why is familiarity misleading?",
          options: [
            "Recognizing information is not the same as being able to recall it.",
            "Familiar material is usually less important on exams.",
            "Students become bored when material is familiar.",
            "Familiar texts are harder to highlight correctly.",
          ],
          correct: 0,
        },
        {
          question: "What can be inferred about why students continue to re-read?",
          options: [
            "Their professors recommend it.",
            "It takes less time than other methods.",
            "It produces better results a week later.",
            "It gives them a false sense of how well they have learned.",
          ],
          correct: 3,
        },
      ],
    },
  ],

  // ── Easier Module 2 ──────────────────────────────────────────
  lower: [
    response("low-r1", W("Did you remember to return the projector to the media office?"), [
      "I'll project it on the screen.",
      "Oh no, it's still in my bag.",
      "The media office is very busy.",
      "I remember the movie well.",
    ], 1),
    response("low-r2", M("How about studying together before the midterm?"), [
      "The midterm is in two weeks.",
      "I studied history last year.",
      "Sure. Does Thursday evening work for you?",
      "Together, they make a good team.",
    ], 2),
    response("low-r3", W("Why is the cafeteria so empty today?"), [
      "There's a free lunch at the student festival.",
      "The cafeteria serves pasta on Mondays.",
      "I emptied the trash this morning.",
      "Because I ate there yesterday.",
    ], 0),
    response("low-r4", M("Could you send me the link to the class website?"), [
      "The website looks nice.",
      "I sent the class a letter.",
      "It's linked to the library.",
      "Sure, I'll email it to you right now.",
    ], 3),
    response("low-r5", W("I've lost my student ID card."), [
      "My card is in my wallet.",
      "You can get a new one at the main office.",
      "I lost the game last night.",
      "Students need to study.",
    ], 1),
    {
      id: "low-ann",
      type: "announcement",
      title: "Campus Shuttle Announcement",
      lines: [
        M("Attention, students. Because of road repairs on College Avenue, the campus shuttle will use a different route for the next two weeks. The stop in front of the science building will be closed. Instead, please use the temporary stop next to the main parking lot. Shuttles will run every fifteen minutes instead of every ten, so please plan for a little extra travel time. The normal route will return on the twenty-first."),
      ],
      questions: [
        {
          question: "Why is the shuttle route changing?",
          options: [
            "The science building is closed.",
            "There are repairs on a road.",
            "A new parking lot is being built.",
            "There are not enough drivers.",
          ],
          correct: 1,
        },
        {
          question: "What will be different about the shuttle service?",
          options: [
            "Shuttles will come less often.",
            "Shuttles will stop running in the evening.",
            "Students will need a ticket.",
            "Shuttles will go to College Avenue.",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "low-talk",
      type: "talk",
      title: "Environmental Science: Urban Heat Islands",
      lines: [
        W("Have you ever noticed that cities often feel hotter than the countryside around them? Scientists call this the urban heat island effect. There are a few reasons for it. First, materials like concrete and asphalt absorb heat during the day and release it slowly at night, so cities stay warm even after the sun goes down. Second, cities have fewer trees and plants. Plants cool the air by releasing water through their leaves. Third, cars, air conditioners, and factories all produce heat. Because of this, a large city can be several degrees warmer than nearby rural areas. To reduce the effect, some cities are planting more trees and painting roofs white, since white surfaces reflect sunlight instead of absorbing it."),
      ],
      questions: [
        {
          question: "What is the talk mainly about?",
          options: [
            "Why cities are warmer than rural areas",
            "How to build better roads",
            "The history of air conditioning",
            "Why trees grow faster in the countryside",
          ],
          correct: 0,
        },
        {
          question: "According to the professor, why do cities stay warm at night?",
          options: [
            "Because people turn on their heaters",
            "Because there is more wind in the city",
            "Because concrete and asphalt release heat slowly",
            "Because the sun sets later in cities",
          ],
          correct: 2,
        },
        {
          question: "How do plants help cool the air?",
          options: [
            "They block the wind.",
            "They release water through their leaves.",
            "They absorb heat from cars.",
            "They make the ground darker.",
          ],
          correct: 1,
        },
        {
          question: "Why are some cities painting roofs white?",
          options: [
            "White paint is cheaper.",
            "White roofs last longer in the rain.",
            "White roofs are easier to see from planes.",
            "White surfaces reflect sunlight.",
          ],
          correct: 3,
        },
      ],
    },
  ],
};
